<script setup lang="ts">
import { CheckCircle2, KeyRound, LoaderCircle, ShieldCheck, Terminal } from "lucide-vue-next"
import { computed, onMounted, ref } from "vue"
import { useRoute } from "vue-router"

import AppSelect from "../components/AppSelect.vue"
import { ApiError, apiRequest } from "../lib/api"
import { useSessionStore } from "../stores/session"

const route = useRoute()
const session = useSessionStore()
const userCode = ref("")
const workspaceId = ref("")
const submitting = ref(false)
const approved = ref(false)
const errorMessage = ref("")

const normalizedCode = computed(() => {
  const value = userCode.value.toUpperCase().replace(/[^A-Z2-9]/g, "").slice(0, 8)
  return value.length > 4 ? `${value.slice(0, 4)}-${value.slice(4)}` : value
})

const canApprove = computed(
  () => /^[A-HJ-KM-NP-Z2-9]{4}-[A-HJ-KM-NP-Z2-9]{4}$/.test(normalizedCode.value) && Boolean(workspaceId.value),
)
const workspaceOptions = computed(() =>
  (session.me?.workspaces ?? []).map((workspace) => ({
    value: workspace.id,
    label: `${workspace.name} · ${workspace.role}`,
  })),
)

onMounted(() => {
  const code = typeof route.query.code === "string" ? route.query.code : ""
  userCode.value = code
  workspaceId.value = session.workspace?.id ?? ""
})

function formatCode(): void {
  userCode.value = normalizedCode.value
}

async function approve(): Promise<void> {
  if (!canApprove.value) return
  submitting.value = true
  errorMessage.value = ""
  try {
    await apiRequest<{ status: "approved" }>("/v1/auth/device/approve", {
      method: "POST",
      csrfToken: session.csrfToken,
      body: { user_code: normalizedCode.value, workspace_id: workspaceId.value },
    })
    approved.value = true
  } catch (error) {
    errorMessage.value = error instanceof ApiError ? error.message : "设备授权失败，请检查代码后重试。"
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <section class="device-page" data-screen-label="设备授权">
    <div class="device-card">
      <div v-if="approved" class="device-success" role="status">
        <span class="device-icon device-icon--success"><CheckCircle2 :size="27" /></span>
        <span class="eyebrow">Device authorized</span>
        <h1>设备已授权</h1>
        <p>可以关闭此页面并返回终端。CLI 会继续保存凭据、安装 Skill 并配置 MCP。</p>
        <div class="device-code-display"><Terminal :size="18" /><code>{{ normalizedCode }}</code></div>
      </div>

      <template v-else>
        <header class="device-heading">
          <span class="device-icon"><KeyRound :size="25" /></span>
          <div><span class="eyebrow">ByreCC CLI</span><h1>授权这台设备</h1></div>
        </header>
        <p class="device-copy">只在你刚刚运行了 <code>byrectl install</code> 时继续。授权后将创建一份最小权限、可随时撤销的访问凭据。</p>

        <form class="form-stack" @submit.prevent="approve">
          <label class="field">
            <span>终端显示的设备代码</span>
            <input
              v-model="userCode"
              class="device-code-input"
              maxlength="9"
              autocomplete="one-time-code"
              placeholder="ABCD-EFGH"
              required
              @input="formatCode"
            />
          </label>
          <label class="field">
            <span>Workspace</span>
            <AppSelect v-model="workspaceId" aria-label="Workspace" :options="workspaceOptions" />
          </label>

          <div class="permission-summary">
            <div><span>权限</span><strong>只读</strong></div>
            <div><span>Scope</span><strong>content:read</strong></div>
            <div><span>平台</span><strong>六平台</strong></div>
          </div>
          <div class="device-security-note"><ShieldCheck :size="17" /><span>API Key 不会显示在浏览器中；CLI 仅会收到一次性安装凭据。</span></div>
          <p v-if="errorMessage" class="form-error" role="alert">{{ errorMessage }}</p>
          <button class="primary-button full-button" :disabled="submitting || !canApprove">
            <LoaderCircle v-if="submitting" class="spin" :size="17" />
            <ShieldCheck v-else :size="17" />
            授权设备
          </button>
        </form>
      </template>
    </div>
  </section>
</template>
